import type { SupabaseClient } from '@supabase/supabase-js';

type Product = {
  id: string;
  name: string;
  price: number;
  tax_rate: number;
  unit?: string | null;
};

type InvoiceTriggerContext = 'sample_collection' | 'contract_creation';

type OrganizationInvoiceSettings = {
  auto_invoice_on_sample_collection?: boolean | null;
  auto_invoice_on_contract_creation?: boolean | null;
};

type OrganizationFeatureSettings = {
  feature_accounting?: boolean | null;
};

const isMissingTriggerContextColumnError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const maybeError = error as { code?: string; message?: string };
  return (
    maybeError.code === '42703' &&
    typeof maybeError.message === 'string' &&
    maybeError.message.includes('product_sample_rules.trigger_context')
  );
};

const roundCurrency = (value: number) => Number.parseFloat(value.toFixed(2));

const addDaysToIsoDate = (baseDate: Date, days: number) => {
  // Der Fälligkeitstermin wird aus dem Rechnungsdatum plus konfigurierter Frist berechnet.
  const dueDate = new Date(baseDate);
  dueDate.setDate(dueDate.getDate() + days);
  return dueDate.toISOString().split('T')[0];
};

const isMissingTableOrColumnError = (error: unknown, tableOrColumnName: string): boolean => {
  if (!error || typeof error !== 'object') return false;
  const maybeError = error as { code?: string; message?: string };
  return (
    (maybeError.code === '42P01' || maybeError.code === '42703') &&
    typeof maybeError.message === 'string' &&
    maybeError.message.includes(tableOrColumnName)
  );
};

async function resolveInvoiceDefaultDueDays(supabase: SupabaseClient, organizationId: string) {
  // Für automatische Rechnungen greifen wir die gleiche Zahlungsfrist wie im Rechnungsformular auf.
  const defaultDueDays = 14;

  const { data, error } = await supabase
    .from('organization_accounting_settings')
    .select('invoice_default_due_days')
    .eq('organization_id', organizationId)
    .maybeSingle();

  if (error) {
    // Fallback auf 14 Tage für ältere Datenbanken ohne die neue Tabelle/Spalte.
    if (isMissingTableOrColumnError(error, 'organization_accounting_settings') || isMissingTableOrColumnError(error, 'invoice_default_due_days')) {
      return defaultDueDays;
    }

    throw error;
  }

  const resolvedDueDays = data?.invoice_default_due_days;
  if (typeof resolvedDueDays !== 'number' || Number.isNaN(resolvedDueDays) || resolvedDueDays < 0) {
    return defaultDueDays;
  }

  return resolvedDueDays;
}

async function getNextInvoiceNumber(supabase: SupabaseClient, organizationId: string) {
  const { data: latestInvoice, error } = await supabase
    .from('invoices')
    .select('invoice_number')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;

  const currentYear = new Date().getFullYear();
  const current = latestInvoice?.invoice_number ?? '';
  const match = current.match(/(\d+)$/);
  const next = match ? Number.parseInt(match[1], 10) + 1 : 1;

  return `RE-${currentYear}-${String(next).padStart(4, '0')}`;
}

async function createInvoiceWithProducts({
  supabase,
  organizationId,
  customerId,
  createdBy,
  products,
  reference,
}: {
  supabase: SupabaseClient;
  organizationId: string;
  customerId: string;
  createdBy: string;
  products: Product[];
  reference: string;
}) {
  if (products.length === 0) {
    return { created: false } as const;
  }

  const uniqueProducts = Array.from(new Map(products.map((product) => [product.id, product])).values());

  const invoiceItemsPayload = uniqueProducts.map((product) => {
    const unitPrice = roundCurrency(product.price);
    const amount = unitPrice;
    const taxAmount = roundCurrency((amount * (product.tax_rate ?? 0)) / 100);

    return {
      organization_id: organizationId,
      product_id: product.id,
      product_name: product.name,
      description: null,
      quantity: 1,
      unit_price: unitPrice,
      tax_rate: product.tax_rate ?? 0,
      amount,
      discount_amount: 0,
      unit_name: product.unit?.trim() || 'Stück',
      taxAmount,
    };
  });

  const totalNet = roundCurrency(invoiceItemsPayload.reduce((sum, item) => sum + item.amount, 0));
  const totalTax = roundCurrency(invoiceItemsPayload.reduce((sum, item) => sum + item.taxAmount, 0));
  const totalGross = roundCurrency(totalNet + totalTax);

  const invoiceNumber = await getNextInvoiceNumber(supabase, organizationId);
  const invoiceDate = new Date();
  const dueDays = await resolveInvoiceDefaultDueDays(supabase, organizationId);
  const dueDate = addDaysToIsoDate(invoiceDate, dueDays);

  const { data: createdInvoice, error: invoiceError } = await supabase
    .from('invoices')
    .insert({
      organization_id: organizationId,
      customer_id: customerId,
      invoice_number: invoiceNumber,
      invoice_date: invoiceDate.toISOString().split('T')[0],
      due_date: dueDate,
      // Automatisch erzeugte Rechnungen starten explizit im Status "created" (Erstellt) statt Entwurf.
      status: 'created',
      payment_status: 'open',
      currency: 'EUR',
      total_net: totalNet,
      total_tax: totalTax,
      total_gross: totalGross,
      discount_amount: 0,
      voucher_amount: 0,
      voucher_id: null,
      notes: null,
      reference,
      created_by: createdBy,
    })
    .select('id')
    .single();

  if (invoiceError) throw invoiceError;

  const payload = invoiceItemsPayload.map((item) => ({
    invoice_id: createdInvoice.id,
    organization_id: item.organization_id,
    product_id: item.product_id,
    product_name: item.product_name,
    description: item.description,
    quantity: item.quantity,
    unit_price: item.unit_price,
    tax_rate: item.tax_rate,
    amount: item.amount,
    discount_amount: item.discount_amount,
    unit_name: item.unit_name,
  }));

  const { error: itemsError } = await supabase.from('invoice_items').insert(payload);
  if (itemsError) throw itemsError;

  return { created: true, invoiceId: createdInvoice.id } as const;
}

async function canCreateAutomaticInvoiceForOrganization({
  supabase,
  organizationId,
  triggerContext,
}: {
  supabase: SupabaseClient;
  organizationId: string;
  triggerContext: InvoiceTriggerContext;
}) {
  // Sicherheitsprüfung: automatische Rechnungen sind nur erlaubt,
  // wenn Buchhaltung aktiv ist und der jeweilige Workflow eingeschaltet wurde.
  // Die Buchhaltungs-Funktion liegt primär in organization_features,
  // ältere Datenbanken können sie aber noch in organizations führen.
  const { data: organizationFeatures, error: organizationFeaturesError } = await supabase
    .from('organization_features')
    .select('feature_accounting')
    .eq('organization_id', organizationId)
    .maybeSingle<OrganizationFeatureSettings>();

  if (organizationFeaturesError && !isMissingTableOrColumnError(organizationFeaturesError, 'organization_features')) {
    throw organizationFeaturesError;
  }

  const { data: organization, error } = await supabase
    .from('organizations')
    // Nur die Auto-Invoice-Flags aus organizations lesen,
    // damit kein Fehler bei Instanzen ohne organizations.feature_accounting entsteht.
    .select('auto_invoice_on_sample_collection, auto_invoice_on_contract_creation')
    .eq('id', organizationId)
    .maybeSingle<OrganizationInvoiceSettings>();

  if (error) throw error;

  // Quelle für Buchhaltungszugriff ist organization_features.
  // Falls die View/Tabelle in älteren Setups fehlt, deaktivieren wir automatische Rechnungen sicherheitshalber.
  const hasAccountingFeature = Boolean(organizationFeatures?.feature_accounting);
  const workflowEnabled =
    triggerContext === 'sample_collection'
      ? Boolean(organization?.auto_invoice_on_sample_collection)
      : Boolean(organization?.auto_invoice_on_contract_creation);

  return hasAccountingFeature && workflowEnabled;
}

async function loadRuleBasedProducts({
  supabase,
  organizationId,
  sampleTypeId,
  substanceCategoryIds,
  collectionMode,
  triggerContext,
}: {
  supabase: SupabaseClient;
  organizationId: string;
  sampleTypeId: string;
  substanceCategoryIds: string[];
  collectionMode?: string | null;
  triggerContext: InvoiceTriggerContext;
}) {
  if (substanceCategoryIds.length === 0) {
    return [] as Product[];
  }

  let rules: Array<{
    product_id: string;
    collection_mode?: string | null;
    trigger_context?: string | null;
    sample_type_id?: string | null;
    substance_category_id?: string | null;
  }> = [];

  let rulesQuery = supabase
    .from('product_sample_rules')
    .select('product_id, collection_mode, trigger_context, sample_type_id, substance_category_id')
    .eq('organization_id', organizationId)
    .eq('active', true);

  if (triggerContext !== 'contract_creation') {
    // Für Probenentnahmen gelten weiterhin nur exakt passende Regeln.
    rulesQuery = rulesQuery.eq('sample_type_id', sampleTypeId).in('substance_category_id', substanceCategoryIds);
  }

  const { data: rulesWithContext, error: rulesWithContextError } = await rulesQuery;

  if (rulesWithContextError) {
    if (!isMissingTriggerContextColumnError(rulesWithContextError)) {
      throw rulesWithContextError;
    }

    let legacyQuery = supabase
      .from('product_sample_rules')
      .select('product_id, collection_mode, sample_type_id, substance_category_id')
      .eq('organization_id', organizationId)
      .eq('active', true);

    if (triggerContext !== 'contract_creation') {
      legacyQuery = legacyQuery.eq('sample_type_id', sampleTypeId).in('substance_category_id', substanceCategoryIds);
    }

    const { data: legacyRules, error: legacyRulesError } = await legacyQuery;

    if (legacyRulesError) throw legacyRulesError;
    rules = (legacyRules ?? []).map((rule) => ({ ...rule, trigger_context: 'both' }));
  } else {
    rules = rulesWithContext ?? [];
  }

  const normalizedCollectionMode = collectionMode?.trim().toLowerCase() ?? '';
  // Wir ermitteln zuerst streng nach Entnahmemodus und fallen bei Bedarf auf allgemeine Regeln zurück.
  const filterRuleProductIds = (requireCollectionModeMatch: boolean) =>
    Array.from(
      new Set(
        rules
          .filter((rule) => {
            const mode = rule.collection_mode?.trim().toLowerCase();
            const ruleContext = rule.trigger_context ?? 'both';
            const contextMatches = ruleContext === 'both' || ruleContext === triggerContext;

            if (!contextMatches) {
              return false;
            }

            // Für Vertragserstellung unterstützen wir auch globale Regeln ohne Probenart/Substanzkategorie.
            if (triggerContext === 'contract_creation') {
              const sampleTypeMatches = !rule.sample_type_id || rule.sample_type_id === sampleTypeId;
              const categoryMatches = !rule.substance_category_id || substanceCategoryIds.includes(rule.substance_category_id);
              if (!sampleTypeMatches || !categoryMatches) {
                return false;
              }
            }

            if (!mode) {
              return true;
            }

            if (!requireCollectionModeMatch) {
              return true;
            }

            return mode === normalizedCollectionMode;
          })
          .map((rule) => rule.product_id),
      ),
    );

  let ruleProductIds = filterRuleProductIds(true);

  // Fallback: Falls keine mode-spezifische Regel passt (oder kein Modus übergeben wurde),
  // verwenden wir alle passenden Trigger-Regeln, damit Rechnungen nicht ausbleiben.
  if (ruleProductIds.length === 0) {
    ruleProductIds = filterRuleProductIds(false);
  }

  if (ruleProductIds.length === 0) {
    return [] as Product[];
  }

  const { data: products, error: productsError } = await supabase
    .from('products')
    .select('id, name, price, tax_rate, unit, active')
    .eq('organization_id', organizationId)
    .eq('active', true)
    .in('id', ruleProductIds);

  if (productsError) throw productsError;
  return (products ?? []) as Product[];
}

export async function createAutomaticSampleInvoice({
  supabase,
  organizationId,
  sampleId,
  createdBy,
  enabled,
  collectionMode,
}: {
  supabase: SupabaseClient;
  organizationId: string;
  sampleId: string;
  createdBy: string;
  enabled: boolean;
  collectionMode?: string | null;
}) {
  if (!enabled) return { created: false, reason: 'disabled' } as const;

  const organizationAllowsAutomaticInvoicing = await canCreateAutomaticInvoiceForOrganization({
    supabase,
    organizationId,
    triggerContext: 'sample_collection',
  });

  if (!organizationAllowsAutomaticInvoicing) {
    return { created: false, reason: 'organization_disabled' } as const;
  }

  const { data: sample, error: sampleError } = await supabase
    .from('customer_samples')
    .select('id, customer_id, sample_type_id')
    .eq('id', sampleId)
    .single();

  if (sampleError) throw sampleError;

  const { data: substanceRows, error: substanceError } = await supabase
    .from('customer_sample_substances')
    .select('substances!inner(category_id)')
    .eq('sample_id', sampleId);

  if (substanceError) throw substanceError;

  let categoryIds = Array.from(
    new Set((substanceRows ?? []).map((row: any) => row.substances?.category_id).filter(Boolean)),
  );

  if (categoryIds.length === 0) {
    const { data: contractSample, error: contractSampleError } = await supabase
      .from('contract_samples')
      .select('contract_id')
      .eq('sample_id', sampleId)
      .maybeSingle();

    if (contractSampleError) throw contractSampleError;

    if (contractSample?.contract_id) {
      const { data: contractCategories, error: contractCategoriesError } = await supabase
        .from('contract_substance_categories')
        .select('substance_category_id')
        .eq('contract_id', contractSample.contract_id);

      if (contractCategoriesError) throw contractCategoriesError;

      categoryIds = Array.from(
        new Set((contractCategories ?? []).map((row) => row.substance_category_id).filter(Boolean)),
      );
    }
  }

  if (categoryIds.length === 0) return { created: false, reason: 'no_categories' } as const;

  const products = await loadRuleBasedProducts({
    supabase,
    organizationId,
    sampleTypeId: sample.sample_type_id,
    substanceCategoryIds: categoryIds,
    collectionMode,
    triggerContext: 'sample_collection',
  });

  if (products.length === 0) return { created: false, reason: 'no_rules' } as const;

  return createInvoiceWithProducts({
    supabase,
    organizationId,
    customerId: sample.customer_id,
    createdBy,
    products,
    reference: `Automatische Rechnung - Probe ${sampleId}`,
  });
}

export async function createAutomaticContractInvoice({
  supabase,
  organizationId,
  contractId,
  customerId,
  sampleTypeId,
  substanceCategoryIds,
  createdBy,
  enabled,
}: {
  supabase: SupabaseClient;
  organizationId: string;
  contractId: string;
  customerId: string;
  sampleTypeId: string;
  substanceCategoryIds: string[];
  createdBy: string;
  enabled: boolean;
}) {
  if (!enabled) return { created: false, reason: 'disabled' } as const;

  const organizationAllowsAutomaticInvoicing = await canCreateAutomaticInvoiceForOrganization({
    supabase,
    organizationId,
    triggerContext: 'contract_creation',
  });

  if (!organizationAllowsAutomaticInvoicing) {
    return { created: false, reason: 'organization_disabled' } as const;
  }

  if (substanceCategoryIds.length === 0) return { created: false, reason: 'no_categories' } as const;

  const products = await loadRuleBasedProducts({
    supabase,
    organizationId,
    sampleTypeId,
    substanceCategoryIds,
    triggerContext: 'contract_creation',
  });

  if (products.length === 0) return { created: false, reason: 'no_rules' } as const;

  return createInvoiceWithProducts({
    supabase,
    organizationId,
    customerId,
    createdBy,
    products,
    reference: `Automatische Rechnung - Vertrag ${contractId}`,
  });
}
